/**
 * `/__ui/admin` — the visual-regression and hit-test harness for M8 P1/P2, the same trick the
 * board, notifications, forms and automations harnesses use. It renders with **no session, no
 * api and no database**, so a served static build can be driven by
 * `tools/browser/verify-admin-local.mjs` and diffed against
 * `discovery/ui-reference/admin/p1p2-*.png`.
 *
 * `?screen=` selects a surface:
 *   `users` (default) · `invitations` · `teams` · `general` · `permissions` · `security` ·
 *   `tokens` · `audit` · `usage` · `board-permissions` · `share` · `invite` · `profile` ·
 *   `workspace-members`
 */
import { useEffect, useMemo, useState } from 'react';
import { AdminShell, type AdminSectionId } from './AdminShell';
import { BoardPermissionsDialog, WorkspaceMembers } from './sharing';
import { createFixtureSource } from './fixture';
import {
  ActiveImpersonationsView,
  ImpersonationBannerView,
  ViewAsDialogView,
  type ActiveImpersonationRow,
} from './impersonation';
import { createFixtureConnections } from './connections/fixture';
import { InviteDialog } from '../profile/InviteDialog';
import { ProfilePage, type ProfileMe, type ProfileSource } from '../profile/ProfilePage';
import './admin.css';

const ADMIN_SCREENS: AdminSectionId[] = [
  'users',
  'invitations',
  'teams',
  'general',
  'permissions',
  'security',
  'tokens',
  'audit',
  'usage',
  'connections',
];

const FIXTURE_ME: ProfileMe = {
  id: '2',
  name: 'Roberto',
  email: 'roberto@example.com',
  role: 'admin',
  title: 'CTO',
  phone: null,
  mobilePhone: null,
  location: null,
  birthday: null,
  photoUrl: null,
  timezone: 'Europe/Madrid',
  locale: 'en',
  theme: 'light',
  weekStart: null,
  dateFormat: null,
  teams: [{ id: '555487', name: 'MGMT' }],
};

function fixtureProfile(): ProfileSource {
  let me = { ...FIXTURE_ME };
  return {
    me: async () => me,
    update: async (patch) => {
      me = { ...me, ...(patch as Partial<ProfileMe>) };
      return me;
    },
    changePassword: async () => undefined,
    sessions: async () => [
      { id: '1', ip: '127.0.0.1', userAgent: 'Chromium', lastSeenAt: '2026-09-10T10:00:00Z', current: true },
      { id: '2', ip: '10.0.0.4', userAgent: 'Firefox', lastSeenAt: '2026-09-01T10:00:00Z', current: false },
    ],
    revokeSession: async () => undefined,
    logoutOthers: async () => ({ revoked: 1 }),
  };
}

export function AdminHarness(): React.ReactElement {
  const params = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search);
  const screen = params.get('screen') ?? 'users';
  const source = useMemo(() => createFixtureSource(), []);
  const profileSource = useMemo(() => fixtureProfile(), []);
  const fixtureConnections = useMemo(() => createFixtureConnections(), []);
  const [section, setSection] = useState<AdminSectionId>(
    (ADMIN_SCREENS.includes(screen as AdminSectionId) ? screen : 'users') as AdminSectionId,
  );
  /*
   * P-013. The dialog screens used to be handed `onClose={() => undefined}`, which is a fixture
   * that *cannot* close: pressing Escape on `?screen=invite` did nothing, and the sweep could
   * not tell a dialog that ignores Escape from a harness that ignores its own callback. The
   * initial render is unchanged — every pixel verifier still opens straight onto the dialog —
   * and closing now actually closes, which is the behaviour under test.
   */
  const [dialogClosed, setDialogClosed] = useState(false);
  const closeDialog = () => setDialogClosed(true);

  if (screen === 'board-permissions') {
    return (
      <div className="ez-ui-harness-dialog" data-testid="ui-admin-board-permissions">
        <BoardPermissionsDialog source={source} boardId="1000000000000" onClose={() => undefined} />
      </div>
    );
  }

  /*
   * P-241: `/__ui/admin?screen=share` and `?screen=share-public` are gone with
   * `ShareBoardDialog`. The reachable dialog is `modules/boards/BoardSubscribersDialog`, which
   * has its own capture and its own harness.
   */

  if (screen === 'invite') {
    return (
      <div className="ez-ui-harness-dialog" data-testid="ui-admin-invite">
        {dialogClosed ? null : (
          <InviteDialog source={source} productName="Eli Z Monday" canInviteAdmins onClose={closeDialog} />
        )}
      </div>
    );
  }

  if (screen === 'invite-member') {
    return (
      <div className="ez-ui-harness-dialog" data-testid="ui-admin-invite-member">
        {dialogClosed ? null : (
          <InviteDialog
            source={source}
            productName="Eli Z Monday"
            canInviteAdmins={false}
            onClose={closeDialog}
          />
        )}
      </div>
    );
  }

  if (screen === 'profile') {
    return (
      <div style={{ minHeight: '100vh' }} data-testid="ui-admin-profile">
        <ProfilePage source={profileSource} />
      </div>
    );
  }

  /*
   * M8-P3 (T-9u) — the "View as user" surfaces, with **no session and no api**: the banner over
   * a stack of overlays (so the z-index claim can be hit-tested rather than asserted), the
   * confirmation dialog, and the Security screen's Active impersonations card.
   *
   * `?screen=impersonation` renders all three at once on purpose: the one thing the banner has
   * to prove is that nothing the product can raise covers it.
   */
  if (screen === 'impersonation') {
    return <ImpersonationFixture />;
  }

  if (screen === 'workspace-members') {
    return (
      <div className="ez-ui-harness-dialog" data-testid="ui-admin-workspace-members">
        <WorkspaceMembers source={source} workspaceId="1" canManage />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh' }} data-testid="ui-admin">
      <AdminShell source={source} connections={fixtureConnections} section={section} onSection={setSection} />
    </div>
  );
}

/** The M8-P3 fixture: the bar, a modal-height stack under it, the dialog, and the admin card. */
function ImpersonationFixture(): React.ReactElement {
  const source = useMemo(() => createFixtureSource({ canGrantSuperAdmin: true }), []);
  /**
   * The dialog starts open, and the synthetic overlay stack is tied to it: while it is open the
   * banner has a modal backdrop and a toast band over the page to beat, and dismissing it
   * clears the stack so the rest of the screen can be clicked. That is also how the product
   * behaves — an overlay that is gone stops intercepting.
   */
  const [dialogOpen, setDialogOpen] = useState(true);
  const [rows, setRows] = useState<ActiveImpersonationRow[]>([]);
  const [exited, setExited] = useState(false);

  useEffect(() => {
    void source.activeImpersonations().then(setRows);
  }, [source]);

  const state = {
    impersonator: { id: '1', name: 'Nico Nuzzi' },
    target: { id: '5', name: 'Tom Ignatovsky', role: 'member' },
    expiresAt: new Date(Date.UTC(2026, 8, 10, 12, 0, 0)).toISOString(),
    viewOnly: true,
  };

  return (
    <div style={{ minHeight: '100vh' }} data-testid="ui-admin-impersonation">
      <div className="ez-app" data-testid="app-shell" style={{ height: '100vh' }}>
        {exited ? null : (
          <ImpersonationBannerView state={state} onExit={() => setExited(true)} />
        )}
        <div
          className="ez-app__body ez-impersonation-fixture__body"
          style={{ flex: '1 1 auto', position: 'relative' }}
        >
          <main className="ez-app__main" style={{ padding: 24, width: '100%' }} data-testid="app-main">
            <div className="ez-admin-card" data-testid="admin-security-impersonations">
              <h3>Active impersonations</h3>
              <ActiveImpersonationsView
                rows={rows}
                onEnd={(sessionId) => {
                  void source.endImpersonation(sessionId).then(() => {
                    void source.activeImpersonations().then(setRows);
                  });
                }}
              />
            </div>
          </main>
        </div>
      </div>

      {/*
       * The overlay stack the banner has to beat: a modal backdrop and a toast band, both
       * body-level fixed surfaces at the product's own layers. The modal sits at
       * `--ez-layer-modal-backdrop` and **before** the confirmation dialog in the DOM, so the
       * dialog (same layer, later) is still operable — two modals never cover each other in
       * the real product either. The banner has to beat both regardless.
       */}
      {dialogOpen ? (
        <>
          <div className="ez-impersonation-fixture__modal" data-testid="fixture-modal" />
          <div className="ez-impersonation-fixture__toast" data-testid="fixture-toast" />
        </>
      ) : null}

      {dialogOpen ? (
        <div className="ez-dialog-backdrop" data-testid="view-as-backdrop">
          <ViewAsDialogView
            user={{ id: '5', name: 'Tom Ignatovsky', email: 'tom@example.com', role: 'member' }}
            onCancel={() => setDialogOpen(false)}
            onConfirm={() => setDialogOpen(false)}
          />
        </div>
      ) : null}
    </div>
  );
}
